import { useState } from 'react';

import { FlagCode, FlagSeverity, type ReviewItemResponse } from '../../api';
import { DataTable, Form, FormError, TextField, type Column } from '../../components';
import { formatCpf, formatDate } from '../../format';
import {
  useApproveReviewItem,
  useCorrectEffectiveDate,
  useRejectReviewItem,
  useReturnReviewItem,
} from '../queries';
import {
  ACTION_LABELS,
  CHANNEL_LABELS,
  FLAG_LABELS,
  REVIEW_COPY,
  SEVERITY_LABELS,
  identityWasNotChecked,
  wasDateCorrected,
} from './reviewQueue';

/**
 * One listing of movements, and the four things an analyst can do to one.
 *
 * The same component renders all three listings, because they are the same rows
 * read three ways. What differs is what may be done to them, and that is a prop
 * rather than three near-identical tables: the late arrivals in particular are
 * deliberately read-only here -- offering a correction on one would imply a
 * repair path for a month the insurer already has, which is a decision nobody
 * has taken yet.
 *
 * **Rejecting, returning and correcting a date open a form in place.** Each of
 * them needs something typed -- a reason the customer will read, or the date the
 * paperwork says -- and a `window.prompt` would neither be accessible nor let
 * the analyst see the row while they write it. The form appears above the table
 * with the movement named, and the table stays where it is.
 */

type Pending = { readonly deltaId: string; readonly kind: 'reject' | 'return' | 'correct' };

function flagSummary(item: ReviewItemResponse) {
  if (item.flags.length === 0) {
    return <span className="table__muted">—</span>;
  }
  return (
    <ul className="review__flags">
      {item.flags.map((flag) => (
        <li key={`${flag.code}-${flag.field ?? ''}`}>
          <span
            className={
              flag.severity === FlagSeverity.BLOCKING
                ? 'badge badge--blocking'
                : 'badge badge--warning'
            }
          >
            {SEVERITY_LABELS[flag.severity] ?? flag.severity}
          </span>{' '}
          <strong>{FLAG_LABELS[flag.code] ?? flag.code}</strong>
          {/* The sentence is the server's: it is the only thing that knows why
              this particular movement was flagged, and it is written for
              whoever has to act on it. */}
          <span className="review__flag-message"> {flag.message}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The blocking missing-evidence flag on a movement, if it has one.
 *
 * Its company refuses an undocumented termination's approval outright, so the
 * button would only earn a `409`; it is disabled instead, with the server's own
 * sentence as the reason. Other blocking flags leave it enabled: deciding the
 * movement is how a roster conflict is cleared.
 */
function approvalBlocker(item: ReviewItemResponse) {
  return item.flags.find(
    (flag) => flag.code === FlagCode.MISSING_EVIDENCE && flag.severity === FlagSeverity.BLOCKING,
  );
}

export function ReviewItemsPanel({
  items,
  companyId,
  decidable,
  showCycle = false,
  onOpenDocument,
  onDecided,
  emptyTitle,
}: {
  items: readonly ReviewItemResponse[];
  companyId: string;
  /** False for the late arrivals, which are listed and nothing else. */
  decidable: boolean;
  /** True in the forward view, where the cycle a movement lands in is the point. */
  showCycle?: boolean;
  onOpenDocument: (documentId: string) => void;
  /** Called once a decision has been recorded, and never when it was refused. */
  onDecided?: () => void;
  emptyTitle: string;
}) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');

  const approve = useApproveReviewItem();
  const reject = useRejectReviewItem();
  const hand_back = useReturnReviewItem();
  const correct = useCorrectEffectiveDate();

  const close = () => {
    setPending(null);
    setReason('');
    setEffectiveDate('');
  };

  const open = (deltaId: string, kind: Pending['kind'], currentDate: string) => {
    setPending({ deltaId, kind });
    setReason('');
    setEffectiveDate(currentDate);
  };

  const submitPending = () => {
    if (pending === null) {
      return;
    }
    const decision = { deltaId: pending.deltaId, companyId };
    const onSuccess = () => {
      close();
      onDecided?.();
    };
    if (pending.kind === 'correct') {
      correct.mutate({ ...decision, effectiveDate }, { onSuccess });
      return;
    }
    const mutation = pending.kind === 'reject' ? reject : hand_back;
    mutation.mutate({ ...decision, reason }, { onSuccess });
  };

  const pendingError =
    pending?.kind === 'correct'
      ? correct.error
      : pending?.kind === 'reject'
        ? reject.error
        : hand_back.error;

  const columns: readonly Column<ReviewItemResponse>[] = [
    {
      key: 'action',
      header: 'Movimentação',
      render: (item) => ACTION_LABELS[item.delta.action] ?? item.delta.action,
    },
    {
      key: 'person',
      header: 'Pessoa',
      render: (item) => (
        <>
          {item.delta.full_name ?? '—'}
          <br />
          <code className="table__muted">{formatCpf(item.delta.cpf)}</code>
          {/* Beside the name rather than in the flags column, because it is not a
              flag: nothing is wrong with this movement and nothing is in the way
              of approving it. It is a statement about how much is known about the
              person whose name is directly above it. */}
          {identityWasNotChecked(item) && (
            <>
              <br />
              <span className="badge badge--warning" title={REVIEW_COPY.identityUncheckedHint}>
                {REVIEW_COPY.identityUnchecked}
              </span>
            </>
          )}
        </>
      ),
    },
    {
      key: 'effective_date',
      header: 'Vigência',
      render: (item) => (
        <>
          {formatDate(item.delta.effective_date)}
          {wasDateCorrected(item) && (
            <>
              <br />
              <span className="table__muted">
                {REVIEW_COPY.correctedFrom}{' '}
                {formatDate(item.delta.original_effective_date as string)}
              </span>
            </>
          )}
        </>
      ),
    },
    ...(showCycle
      ? [
          {
            key: 'cycle_month',
            header: 'Entra no ciclo',
            render: (item: ReviewItemResponse) => item.cycle_month,
          },
        ]
      : []),
    {
      key: 'channel',
      header: 'Origem',
      render: (item) => CHANNEL_LABELS[item.channel] ?? item.channel,
    },
    { key: 'flags', header: 'Sinalizações', render: flagSummary },
    {
      key: 'documents',
      header: 'Documento',
      render: (item) =>
        item.document_ids.length === 0 ? (
          <span className="table__muted">—</span>
        ) : (
          <button
            type="button"
            className="button button--ghost"
            onClick={() => onOpenDocument(item.document_ids[0] as string)}
          >
            Ver ({item.document_ids.length})
          </button>
        ),
    },
    ...(decidable
      ? [
          {
            key: 'actions',
            header: 'Ações',
            align: 'end' as const,
            render: (item: ReviewItemResponse) => {
              const blocker = approvalBlocker(item);
              return (
                <span className="table__actions">
                  <button
                    type="button"
                    className="button button--primary"
                    disabled={approve.isPending || blocker !== undefined}
                    {...(blocker !== undefined && { title: blocker.message })}
                    onClick={() =>
                      approve.mutate(
                        { deltaId: item.delta.id, companyId },
                        { onSuccess: () => onDecided?.() },
                      )
                    }
                  >
                    {REVIEW_COPY.approveItem}
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() => open(item.delta.id, 'correct', item.delta.effective_date)}
                  >
                    {REVIEW_COPY.correctDate}
                  </button>
                  <button
                    type="button"
                    className="button button--secondary"
                    onClick={() => open(item.delta.id, 'return', item.delta.effective_date)}
                  >
                    {REVIEW_COPY.returnItem}
                  </button>
                  <button
                    type="button"
                    className="button button--danger"
                    onClick={() => open(item.delta.id, 'reject', item.delta.effective_date)}
                  >
                    {REVIEW_COPY.rejectItem}
                  </button>
                </span>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <FormError error={approve.error} />
      {pending !== null && (
        <Form onSubmit={submitPending} aria-label={labelFor(pending.kind)}>
          <FormError error={pendingError} />
          {pending.kind === 'correct' ? (
            <TextField
              label="Nova vigência"
              hint="A data que a documentação assinada diz. A anterior fica registrada."
              type="date"
              required
              value={effectiveDate}
              onChange={setEffectiveDate}
            />
          ) : (
            <TextField
              label={REVIEW_COPY.reasonLabel}
              hint={REVIEW_COPY.reasonHint}
              required
              value={reason}
              onChange={setReason}
            />
          )}
          <div className="form__actions">
            <button type="submit" className="button button--primary">
              {confirmLabelFor(pending.kind)}
            </button>
            <button type="button" className="button button--ghost" onClick={close}>
              Cancelar
            </button>
          </div>
        </Form>
      )}
      <DataTable
        id="review-items"
        caption="Movimentações em análise"
        columns={columns}
        rows={items}
        rowKey={(item) => item.delta.id}
        emptyTitle={emptyTitle}
      />
    </>
  );
}

function labelFor(kind: Pending['kind']): string {
  if (kind === 'correct') {
    return REVIEW_COPY.correctDate;
  }
  return kind === 'reject' ? REVIEW_COPY.rejectItem : REVIEW_COPY.returnItem;
}

function confirmLabelFor(kind: Pending['kind']): string {
  if (kind === 'correct') {
    return REVIEW_COPY.confirmCorrection;
  }
  return kind === 'reject' ? REVIEW_COPY.confirmReject : REVIEW_COPY.confirmReturn;
}
